import { KText } from '@/ui-library/components';
import { useDateFormat } from '@/hooks';
import {
  ApplicantName,
  CancelledBy,
  DepartmentName,
  InterviewLocation,
  InterviewTime,
  InterviewType,
  InterviewerName,
  Jobname,
  NameCell,
  OfficeNames,
  ScheduledBy,
  ScorecardResult,
  StageName,
  TimeSpentInterviewing,
  TimeToFillScorecard,
} from '../cells/InterviewDrilldownCells';

const renderDrilldownHeader = (title: string) => () => (
  <KText size="sm-medium" color="secondary-900" textTransform="none">
    {title}
  </KText>
);

export const useInterviewDrilldownConfig = ({
  drilldownHeading = 'Interviews',
}) => {
  const { dateFormat, timeFormat, currUserTimezone } = useDateFormat();

  const getColumnConfig = () => {
    const columnsMap = {
      interviewName: {
        title: 'Interview name',
        Header: renderDrilldownHeader('Interview name'),
        minWidth: 200,
        accessor: 'interview_name',
        sort: true,
        Cell: ({ row }) => NameCell(row),
      },
      interviewers: {
        title: 'Interviewers',
        Header: renderDrilldownHeader('Interviewers'),
        minWidth: 200,
        accessor: 'interviewer_name',
        sort: true,
        Cell: ({ row }) => InterviewerName(row),
      },
      applicantName: {
        title: 'Applicant name',
        Header: renderDrilldownHeader('Applicant name'),
        minWidth: 200,
        accessor: 'candidate_name',
        sort: true,
        Cell: ({ row }) => ApplicantName(row),
      },
      scorecard: {
        title: 'Scorecard status / Overall evaluation',
        Header: renderDrilldownHeader('Scorecard status / Overall evaluation'),
        id: 'scorecard_rating',
        minWidth: 320,
        accessor: 'scorecard_Rating',
        sort: true,
        Cell: ({ row }) => ScorecardResult(row),
      },
      jobName: {
        title: 'Job name',
        Header: renderDrilldownHeader('Job name'),
        minWidth: 200,
        accessor: 'job_name',
        sort: true,
        Cell: ({ row }) => Jobname(row),
      },
      timeSpent: {
        title: 'Time spent interviewing',
        Header: renderDrilldownHeader('Time spent interviewing'),
        minWidth: 200,
        accessor: 'interview_duration',
        sort: true,
        Cell: ({ row }) => TimeSpentInterviewing(row),
      },
      interviewDuration: {
        title: 'Interview duration',
        Header: renderDrilldownHeader('Interview duration'),
        minWidth: 200,
        accessor: 'interview_duration',
        sort: true,
        Cell: ({ row }) => TimeSpentInterviewing(row),
      },
      fillScorecard: {
        title: 'Time to fill scorecard',
        Header: renderDrilldownHeader('Time to fill scorecard'),
        minWidth: 200,
        accessor: 'time_to_fill_scorecard',
        sort: true,
        Cell: ({ row }) => TimeToFillScorecard(row),
      },
      interviewType: {
        title: 'Interview type',
        Header: renderDrilldownHeader('Interview type'),
        minWidth: 160,
        accessor: 'interview_type',
        sort: true,
        Cell: ({ row }) => InterviewType(row),
      },
      interviewTime: {
        title: 'Interview time',
        Header: renderDrilldownHeader('Interview time'),
        minWidth: 160,
        accessor: 'interview_time',
        sort: true,
        Cell: ({ row }) => (
          <InterviewTime
            row={row}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            currUserTimezone={currUserTimezone}
          />
        ),
      },
      interviewLocation: {
        title: 'Interview location',
        Header: renderDrilldownHeader('Interview location'),
        minWidth: 160,
        accessor: 'interview_location',
        sort: true,
        Cell: ({ row }) => InterviewLocation(row),
      },
      stageName: {
        title: 'Stage name',
        Header: renderDrilldownHeader('Stage name'),
        minWidth: 160,
        accessor: 'stage_name',
        sort: true,
        Cell: ({ row }) => StageName(row),
      },
      departmentName: {
        title: 'Department',
        Header: renderDrilldownHeader('Department'),
        minWidth: 160,
        accessor: 'department_name',
        sort: true,
        Cell: ({ row }) => DepartmentName(row),
      },
      officeNames: {
        title: 'Offices',
        Header: renderDrilldownHeader('Offices'),
        minWidth: 220,
        accessor: 'offices',
        sort: true,
        Cell: ({ row }) => OfficeNames(row),
      },
      scheduledBy: {
        title: 'Scheduled by',
        Header: renderDrilldownHeader('Scheduled by'),
        id: 'scheduled_by',
        minWidth: 180,
        accessor: 'scheduledBy',
        sort: true,
        Cell: ({ row }) => ScheduledBy(row),
      },
      cancelledBy: {
        title: 'Canceled by',
        Header: renderDrilldownHeader('Canceled by'),
        id: 'cancelled_by',
        minWidth: 180,
        accessor: 'cancelledBy',
        sort: true,
        Cell: ({ row }) => CancelledBy(row),
      },
    };

    const commonJobStageDeptOfficesScheduled = [
      columnsMap.jobName,
      columnsMap.stageName,
      columnsMap.departmentName,
      columnsMap.officeNames,
      columnsMap.scheduledBy,
    ];

    const commonInterviewDetails = [
      columnsMap.interviewType,
      columnsMap.interviewTime,
      columnsMap.interviewDuration,
      columnsMap.interviewLocation,
    ];

    const withFirstColumnLocked = (cols: object[]) => [
      { ...cols[0], columnConfig: false },
      ...cols.slice(1),
    ];

    switch (drilldownHeading) {
      case 'Completed interviews':
        return withFirstColumnLocked([
          columnsMap.interviewName,
          columnsMap.interviewers,
          columnsMap.applicantName,
          columnsMap.scorecard,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'Total interviews':
        return withFirstColumnLocked([
          columnsMap.interviewName,
          columnsMap.interviewers,
          columnsMap.applicantName,
          columnsMap.scorecard,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'Interviewed applicants':
      case 'Applicants interviewed':
        return withFirstColumnLocked([
          columnsMap.applicantName,
          columnsMap.interviewers,
          columnsMap.interviewName,
          columnsMap.scorecard,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'Avg. time spent interviewing':
        return withFirstColumnLocked([
          columnsMap.applicantName,
          columnsMap.timeSpent,
          columnsMap.interviewers,
          columnsMap.interviewName,
          columnsMap.scorecard,
          columnsMap.interviewType,
          columnsMap.interviewTime,
          columnsMap.interviewLocation,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'Avg. time to fill scorecard':
        return withFirstColumnLocked([
          columnsMap.applicantName,
          columnsMap.interviewers,
          columnsMap.fillScorecard,
          columnsMap.interviewName,
          columnsMap.scorecard,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'Completed interviews trend':
        return withFirstColumnLocked([
          columnsMap.interviewName,
          columnsMap.interviewers,
          columnsMap.applicantName,
          columnsMap.scorecard,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'Completed interviews by feedback':
        return withFirstColumnLocked([
          columnsMap.applicantName,
          columnsMap.interviewName,
          columnsMap.interviewers,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'Completed interviews by duration':
        return withFirstColumnLocked([
          columnsMap.interviewName,
          columnsMap.interviewers,
          columnsMap.applicantName,
          columnsMap.scorecard,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'Total scorecards':
        return withFirstColumnLocked([
          columnsMap.applicantName,
          columnsMap.interviewers,
          columnsMap.scorecard,
          columnsMap.interviewName,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'STRONG YES':
      case 'STRONG NO':
      case 'YES':
      case 'NO':
        return withFirstColumnLocked([
          columnsMap.applicantName,
          columnsMap.interviewName,
          columnsMap.interviewers,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'Pending scorecards':
        return withFirstColumnLocked([
          columnsMap.applicantName,
          columnsMap.interviewName,
          columnsMap.interviewers,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);

      case 'Canceled interviews':
        return withFirstColumnLocked([
          columnsMap.applicantName,
          columnsMap.interviewName,
          columnsMap.interviewers,
          columnsMap.interviewType,
          columnsMap.interviewTime,
          columnsMap.interviewDuration,
          columnsMap.interviewLocation,
          columnsMap.jobName,
          columnsMap.stageName,
          columnsMap.departmentName,
          columnsMap.officeNames,
          columnsMap.scheduledBy,
          columnsMap.cancelledBy,
        ]);

      default:
        return withFirstColumnLocked([
          columnsMap.applicantName,
          columnsMap.interviewName,
          columnsMap.interviewers,
          ...commonInterviewDetails,
          ...commonJobStageDeptOfficesScheduled,
        ]);
    }
  };

  return { columnConfig: getColumnConfig() };
};
